import { useState } from 'react';
import { radioPhotos, type PhotoCategory, PHOTO_CATEGORIES } from '@/mocks/radioContent';
import { useSwipeGesture } from '@/hooks/useSwipeGesture';

export default function FotosTab() {
  const [activeCategory, setActiveCategory] = useState<PhotoCategory | ''>('');
  const [selectedPhoto, setSelectedPhoto] = useState<number | null>(null);
  const [photoIndex, setPhotoIndex] = useState(0);

  const filteredPhotos = activeCategory
    ? radioPhotos.filter((p) => p.category === activeCategory)
    : radioPhotos;

  const currentPhoto = radioPhotos.find((p) => p.id === selectedPhoto);

  const swipe = useSwipeGesture({
    threshold: 60,
    onSwipeLeft: () => {
      if (photoIndex < filteredPhotos.length - 1) {
        const nextIdx = photoIndex + 1;
        setPhotoIndex(nextIdx);
        setSelectedPhoto(filteredPhotos[nextIdx].id);
      }
    },
    onSwipeRight: () => {
      if (photoIndex > 0) {
        const prevIdx = photoIndex - 1;
        setPhotoIndex(prevIdx);
        setSelectedPhoto(filteredPhotos[prevIdx].id);
      }
    },
  });

  const openPhoto = (id: number) => {
    const idx = filteredPhotos.findIndex((p) => p.id === id);
    setPhotoIndex(idx);
    setSelectedPhoto(id);
  };

  const closePhoto = () => {
    setSelectedPhoto(null);
  };

  return (
    <div className="flex flex-col h-full bg-background-50">
      {/* Header */}
      <div className="safe-top px-4 pt-4 pb-3">
        <h1 className="text-2xl font-bold text-foreground-950">Fotos</h1>
        <p className="text-foreground-500 text-sm mt-0.5">Galería de momentos</p>
      </div>

      {/* Category chips */}
      <div className="px-4 pb-4 overflow-x-auto scrollbar-none">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveCategory('')}
            className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
              !activeCategory
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            Todas
          </button>
          {PHOTO_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setActiveCategory(cat.key)}
              className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                activeCategory === cat.key
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              <i className={`${cat.icon} text-xs`}></i>
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Photo grid */}
      <div className="flex-1 overflow-y-auto px-1 pb-24 scrollbar-none">
        <div className="grid grid-cols-3 gap-1">
          {filteredPhotos.map((photo, idx) => (
            <button
              key={photo.id}
              onClick={() => openPhoto(photo.id)}
              className="aspect-square bg-background-200 overflow-hidden cursor-pointer active:scale-95 transition-transform relative group"
              style={{
                animation: `fadeIn 0.3s ease-out forwards`,
                animationDelay: `${idx * 30}ms`,
                opacity: 0,
              }}
            >
              <img
                src={photo.url}
                alt={photo.caption}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/20 transition-colors flex items-end p-1.5">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                  <i className="ri-heart-3-fill text-background-50 text-xs"></i>
                  <span className="text-background-50 text-[10px] font-semibold">{photo.likes}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Photo viewer modal */}
      {selectedPhoto && currentPhoto && (
        <div className="fixed inset-0 z-[70] bg-foreground-950 flex flex-col" {...swipe}>
          {/* Top bar */}
          <div className="safe-top px-4 py-3 flex items-center justify-between">
            <button
              onClick={closePhoto}
              className="w-9 h-9 rounded-full bg-background-50/20 backdrop-blur-sm flex items-center justify-center cursor-pointer"
            >
              <i className="ri-close-line text-background-50 text-lg"></i>
            </button>
            <span className="text-background-50 text-sm font-medium">
              {photoIndex + 1} / {filteredPhotos.length}
            </span>
            <div className="w-9"></div>
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center justify-center px-2">
            <img
              src={currentPhoto.url}
              alt={currentPhoto.caption}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>

          {/* Bottom info */}
          <div className="safe-bottom px-4 py-4">
            <p className="text-background-50 text-sm leading-relaxed">{currentPhoto.caption}</p>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5">
                <i className="ri-heart-3-line text-background-50/70 text-lg"></i>
                <span className="text-background-50/70 text-sm">{currentPhoto.likes}</span>
              </div>
              <span className="text-background-50/40 text-xs bg-background-50/10 px-2 py-0.5 rounded-full">
                {PHOTO_CATEGORIES.find((c) => c.key === currentPhoto.category)?.label}
              </span>
              <span className="text-background-50/40 text-xs">{currentPhoto.date}</span>
            </div>
            {/* Swipe hint */}
            <p className="text-background-50/30 text-[10px] text-center mt-4">
              Desliza para ver más fotos
            </p>
          </div>
        </div>
      )}
    </div>
  );
}